/**
 * GMA-1623 (CompassCare Web 8/14 — Devices) — pure devices model.
 *
 * Fleet KPI rollup + a COMPUTED Client Safety Impact level (AC: "computed, not
 * hand-set"). Impact answers "how does THIS device's current status affect the
 * client's safety coverage", derived from the device's status + capabilities
 * and the client's OTHER devices, with a gateway cascade (an offline gateway
 * cuts off its dependents).
 *
 * All functions fail safe: bad input yields a benign result (empty summary /
 * "low" impact), never a throw — a malformed device row can't crash the screen.
 */

// Capabilities a device can provide. The two CRITICAL ones are emergency
// detection / communication: losing either with no backup is a Critical impact.
export const DEVICE_CAPABILITIES = Object.freeze([
  "fall_detection",
  "communication",
  "vitals",
  "location",
  "environmental",
  "gateway",
]);

export const CRITICAL_CAPABILITIES = Object.freeze([
  "fall_detection",
  "communication",
]);

// Coverage-impact levels, least -> most severe.
export const SAFETY_IMPACT_LEVELS = Object.freeze([
  "low",
  "moderate",
  "high",
  "critical",
]);

const LOW_BATTERY_PCT = 20;
const WEAK_SIGNAL_PCT = 30;

const isLowBattery = (d) =>
  typeof d.battery === "number" && d.battery <= LOW_BATTERY_PCT;
const isWeakSignal = (d) =>
  typeof d.signal === "number" && d.signal <= WEAK_SIGNAL_PCT;
const isOffline = (d) => d.status === "offline";
// The codebase status/severity token is camelCase "atRisk"; accept the snake
// variant too so a mixed feed can't silently slip past the at-risk buckets.
const isAtRisk = (d) => d.status === "atRisk" || d.status === "at_risk";
const isDegraded = (d) =>
  !isOffline(d) &&
  (isAtRisk(d) ||
    isLowBattery(d) ||
    isWeakSignal(d) ||
    d.firmwarePending === true);
const isHealthy = (d) => !isOffline(d) && !isDegraded(d);

/**
 * Fleet KPI rollup. Buckets intentionally overlap (an online device can be
 * low-battery). `needsAttention` = offline OR at_risk OR low battery OR weak
 * signal OR firmware pending.
 * @param {Array} devices
 */
export function summarizeFleet(devices) {
  const list = Array.isArray(devices) ? devices : [];
  const s = {
    total: 0,
    online: 0,
    offline: 0,
    atRisk: 0,
    lowBattery: 0,
    weakSignal: 0,
    firmwarePending: 0,
    needsAttention: 0,
  };
  for (const d of list) {
    // skip malformed rows — arrays are `typeof "object"` too, so exclude them
    if (!d || typeof d !== "object" || Array.isArray(d)) continue;
    // total counts only valid device objects, so it always reconciles with
    // the online+offline split (never disagrees with the buckets).
    s.total += 1;
    if (isOffline(d)) s.offline += 1;
    else s.online += 1;
    if (isAtRisk(d)) s.atRisk += 1;
    if (isLowBattery(d)) s.lowBattery += 1;
    if (isWeakSignal(d)) s.weakSignal += 1;
    if (d.firmwarePending === true) s.firmwarePending += 1;
    // Compose from the same predicates the health model uses (Copilot #301):
    // needs-attention == offline OR degraded, so the degraded criteria can
    // never drift apart between the two.
    if (isOffline(d) || isDegraded(d)) {
      s.needsAttention += 1;
    }
  }
  return s;
}

// Capabilities as a clean string list — non-string/unknown entries (null,
// numbers, objects) are dropped so they can't skew the impact computation.
// Restrict to the KNOWN capability set (Copilot #301): an unexpected feed
// string would otherwise count as a "lost capability" and inflate the
// computed Client Safety Impact.
const KNOWN_CAPABILITIES = new Set(DEVICE_CAPABILITIES);
const caps = (d) =>
  Array.isArray(d && d.capabilities)
    ? d.capabilities.filter((c) => KNOWN_CAPABILITIES.has(c))
    : [];

/**
 * Compute the Client Safety Impact of `device` given the client's `clientDevices`.
 *
 * - Healthy device -> "low" (no coverage gap).
 * - Online but degraded -> "moderate" (reduced visibility).
 * - Offline (or an offline gateway, which cascades to its dependents):
 *     compute the capabilities knocked out (the device's own, plus — if it is
 *     an offline gateway — those of its cut-off dependents). For each, a backup
 *     is any OTHER healthy device that provides it and is not itself cut off by
 *     the same offline gateway.
 *       - an uncovered CRITICAL capability -> "critical"
 *       - any other uncovered capability   -> "high"
 *       - all lost capabilities covered     -> "low"
 * @returns {"low"|"moderate"|"high"|"critical"}
 */
export function computeClientSafetyImpact(device, clientDevices) {
  if (!device || typeof device !== "object") return "low";
  const others = Array.isArray(clientDevices) ? clientDevices : [];

  if (isHealthy(device)) return "low";
  if (!isOffline(device)) return "moderate"; // online but degraded

  // Offline. Determine the cut-off SET (tracked by object REFERENCE, not id, so
  // dependents with a missing/nullish id are handled correctly): the device
  // itself, plus — if it's a gateway — every dependent that reaches the network
  // through it (gatewayId === device.id). Matching a dependent requires the
  // gateway to have a real id, so `gatewayId === undefined` can't match
  // dependents that simply have no gatewayId.
  const hasValidId = device.id !== null && device.id !== undefined;
  const isGateway = hasValidId && caps(device).includes("gateway");
  const cutOff = new Set([device]);
  if (isGateway) {
    for (const d of others) {
      const depGw = d && d.gatewayId;
      if (depGw !== null && depGw !== undefined && depGw === device.id) {
        cutOff.add(d);
      }
    }
  }

  // Capabilities knocked out = device's own + those of cut-off dependents.
  const lost = new Set(caps(device));
  for (const d of cutOff) {
    if (d !== device) {
      for (const c of caps(d)) lost.add(c);
    }
  }
  if (lost.size === 0) return "low";

  // A backup is any healthy device NOT itself cut off (by reference — so a
  // cut-off dependent, even one with no id, can never be a false backup).
  // One pass over the healthy, non-cut-off devices builds the full backed-up
  // capability set; each lost capability is then a set-membership test
  // (Copilot #301 — was a rescan of `others` per lost capability).
  const backedUp = new Set();
  for (const d of others) {
    if (d && !cutOff.has(d) && isHealthy(d)) {
      for (const c of caps(d)) backedUp.add(c);
    }
  }

  const uncovered = [...lost].filter((c) => !backedUp.has(c));
  if (uncovered.length === 0) return "low";
  if (uncovered.some((c) => CRITICAL_CAPABILITIES.includes(c))) return "critical";
  return "high";
}
